'use client';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import PublicNav from '@/components/PublicNav';
import { ArrowLeft, MapPin, Calendar, Users, Zap, Wrench, Lightbulb, ImagePlus, X, AlertCircle, Loader2, Tag, RotateCcw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { PROBLEM_CATEGORIES, CATEGORY_COLORS } from '@/app/problem-solution-board/components/ProblemFeed';
import { saveDraft, loadDraft, clearDraft, ProblemDraft } from '@/lib/postProblemDraft';

interface FormData {
  problem: string;
  category: string;
  town: string;
  country: string;
  continent: string;
  dateOccurred: string;
  dateFuture: string;
  affectedGroup: string;
  effects: string;
  workaround: string;
  proposedSolution: string;
  picture: File | null;
}

const CONTINENTS = [
  'Africa', 'Antarctica', 'Asia', 'Europe',
  'North America', 'Oceania', 'South America',
];

function wordCount(text: string): number {
  return text.trim() === '' ? 0 : text.trim().split(/\s+/).length;
}

function getTodayISO(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export default function PostProblemPage() {
  const today = getTodayISO();
  const router = useRouter();
  const { user, loading: authLoading } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [draftBanner, setDraftBanner] = useState<ProblemDraft | null>(null);

  const [form, setForm] = useState<FormData>({
    problem: '',
    category: '',
    town: '',
    country: '',
    continent: '',
    dateOccurred: today,
    dateFuture: '',
    affectedGroup: '',
    effects: '',
    workaround: '',
    proposedSolution: '',
    picture: null,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  // Check for a saved draft on mount (only client-side)
  useEffect(() => {
    const draft = loadDraft();
    if (draft) {
      setDraftBanner(draft);
    }
  }, []);

  // Auto-save draft to localStorage whenever form changes
  const autoSaveDraft = useCallback((currentForm: FormData) => {
    saveDraft({
      problem: currentForm.problem,
      category: currentForm.category,
      town: currentForm.town,
      country: currentForm.country,
      continent: currentForm.continent,
      dateOccurred: currentForm.dateOccurred,
      dateFuture: currentForm.dateFuture,
      affectedGroup: currentForm.affectedGroup,
      effects: currentForm.effects,
      workaround: currentForm.workaround,
      proposedSolution: currentForm.proposedSolution,
    });
  }, []);

  // Redirect to login if not authenticated — save draft first
  useEffect(() => {
    if (!authLoading && !user) {
      // Persist whatever the user has typed before redirecting
      autoSaveDraft(form);
      router.replace('/sign-up-login-screen');
    }
  }, [user, authLoading, router, form, autoSaveDraft]);

  function handleChange(field: keyof FormData, value: string) {
    setForm(prev => {
      const updated = { ...prev, [field]: value };
      autoSaveDraft(updated);
      return updated;
    });
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    if (file) {
      setForm(prev => ({ ...prev, picture: file }));
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  }

  function removeImage() {
    setForm(prev => ({ ...prev, picture: null }));
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  /** Restore the saved draft into the form */
  function restoreDraft() {
    if (!draftBanner) return;
    setForm(prev => ({
      ...prev,
      problem: draftBanner.problem,
      category: draftBanner.category,
      town: draftBanner.town,
      country: draftBanner.country,
      continent: draftBanner.continent,
      dateOccurred: draftBanner.dateOccurred || today,
      dateFuture: draftBanner.dateFuture,
      affectedGroup: draftBanner.affectedGroup,
      effects: draftBanner.effects,
      workaround: draftBanner.workaround,
      proposedSolution: draftBanner.proposedSolution,
      picture: null,
    }));
    setDraftBanner(null);
  }

  /** Discard the saved draft */
  function discardDraft() {
    clearDraft();
    setDraftBanner(null);
  }

  function validate(): boolean {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    if (!form.problem.trim()) newErrors.problem = 'Problem description is required.';
    else if (wordCount(form.problem) > 50) newErrors.problem = 'Must be 50 words or fewer.';
    if (!form.category) newErrors.category = 'Please select a category.';
    if (!form.town.trim()) newErrors.town = 'Town is required.';
    if (!form.country.trim()) newErrors.country = 'Country is required.';
    if (!form.continent) newErrors.continent = 'Continent is required.';
    if (!form.dateOccurred) newErrors.dateOccurred = 'Date is required.';
    if (!form.affectedGroup.trim()) newErrors.affectedGroup = 'Affected group is required.';
    else if (wordCount(form.affectedGroup) > 50) newErrors.affectedGroup = 'Must be 50 words or fewer.';
    if (!form.effects.trim()) newErrors.effects = 'Effects are required.';
    if (!form.workaround.trim()) newErrors.workaround = 'Current workaround is required.';
    if (!form.proposedSolution.trim()) newErrors.proposedSolution = 'Proposed solution is required.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    if (!user) {
      autoSaveDraft(form);
      router.replace('/sign-up-login-screen');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const supabase = createClient();

      // Build the insert payload — map camelCase form fields to snake_case DB columns
      const payload: Record<string, any> = {
        user_id: user.id,
        title: form.problem.trim(),
        excerpt: form.problem.trim(),
        full_description: form.problem.trim(),
        town: form.town.trim(),
        country: form.country.trim(),
        continent: form.continent,
        date_occurred: form.dateOccurred || null,
        date_future: form.dateFuture || null,
        affected_group: form.affectedGroup.trim(),
        effects: form.effects.trim(),
        workaround: form.workaround.trim(),
        proposed_solution: form.proposedSolution.trim(),
        problem_status: 'open',
        category: form.category || 'General',
        category_color: CATEGORY_COLORS[form.category] ?? 'bg-gray-100 text-gray-700',
        image_url: null,
        image_alt: null,
      };

      // Upload image to Supabase Storage if provided
      if (form.picture) {
        const ext = form.picture.name.split('.').pop();
        const fileName = `${user.id}-${Date.now()}.${ext}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('problem-images')
          .upload(fileName, form.picture, { cacheControl: '3600', upsert: false });

        if (!uploadError && uploadData) {
          const { data: { publicUrl } } = supabase.storage
            .from('problem-images')
            .getPublicUrl(uploadData.path);
          payload.image_url = publicUrl;
          payload.image_alt = `Image illustrating: ${form.problem.trim().slice(0, 60)}`;
        }
        // If upload fails, continue without image (non-blocking)
      }

      const { error: insertError } = await supabase
        .from('problems')
        .insert(payload);

      if (insertError) {
        console.error('Insert error:', insertError.message);
        setSubmitError(insertError.message || 'Failed to post problem. Please try again.');
        setSubmitting(false);
        return;
      }

      // Clear draft on successful submission
      clearDraft();
      setSubmitted(true);
    } catch (err: any) {
      console.error('Unexpected error:', err);
      setSubmitError('An unexpected error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // cleanup object URL
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Show loading while auth state resolves
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  // Don't render form if not authenticated (redirect is in progress)
  if (!user) return null;

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <PublicNav />
        <div className="max-w-xl mx-auto px-4 py-24 text-center">
          <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6">
            <Zap size={28} className="text-success" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-3">Problem Posted!</h2>
          <p className="text-muted-foreground mb-8">Your problem has been submitted to the community. Entrepreneurs and investors can now view and respond to it.</p>
          <div className="flex gap-3 justify-center">
            <Link href="/problem-solution-board?tab=problems" className="px-5 py-2.5 text-sm font-semibold bg-primary text-primary-foreground rounded-lg hover:bg-cyan-600 transition-colors">
              View Problems Board
            </Link>
            <button
              onClick={() => {
                setSubmitted(false);
                setForm({ problem: '', category: '', town: '', country: '', continent: '', dateOccurred: today, dateFuture: '', affectedGroup: '', effects: '', workaround: '', proposedSolution: '', picture: null });
                setPreviewUrl(null);
              }}
              className="px-5 py-2.5 text-sm font-semibold border border-border text-foreground rounded-lg hover:bg-muted transition-colors"
            >
              Post Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  const problemWords = wordCount(form.problem);
  const affectedWords = wordCount(form.affectedGroup);

  return (
    <div className="min-h-screen bg-background">
      <LoadingSpinner visible={submitting} label="Posting your problem…" />
      <PublicNav />

      {/* Draft Recovery Banner */}
      {draftBanner && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
          <div className="max-w-3xl mx-auto flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                <RotateCcw size={15} className="text-amber-600" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-amber-800">You have an unsaved draft</p>
                <p className="text-xs text-amber-600 mt-0.5">
                  It looks like you were interrupted while filling out this form.
                  {draftBanner.problem.trim() && (
                    <span className="block mt-0.5 truncate italic">
                      &ldquo;{draftBanner.problem.trim().slice(0, 80)}{draftBanner.problem.trim().length > 80 ? '…' : ''}&rdquo;
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 pl-11 sm:pl-0">
              <button
                type="button"
                onClick={restoreDraft}
                className="px-4 py-1.5 text-xs font-bold bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
              >
                Continue where I left off
              </button>
              <button
                type="button"
                onClick={discardDraft}
                className="px-3 py-1.5 text-xs font-semibold text-amber-700 border border-amber-300 rounded-lg hover:bg-amber-100 transition-colors"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="gradient-hero text-white py-10 px-4">
        <div className="max-w-3xl mx-auto">
          <Link href="/" className="inline-flex items-center gap-1.5 text-sm text-cyan-300 hover:text-white mb-4 transition-colors">
            <ArrowLeft size={15} /> Back to Home
          </Link>
          <h1 className="text-3xl font-extrabold tracking-tight mb-2">Post a Problem</h1>
          <p className="text-cyan-200 text-sm">Describe a real-world problem so entrepreneurs and investors can build solutions.</p>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-3xl mx-auto px-4 py-10">
        <form onSubmit={handleSubmit} noValidate className="space-y-8">

          {/* Global submit error */}
          {submitError && (
            <div className="flex items-start gap-3 bg-danger/10 border border-danger/30 rounded-xl px-4 py-3">
              <AlertCircle size={16} className="text-danger mt-0.5 shrink-0" />
              <p className="text-sm text-danger">{submitError}</p>
            </div>
          )}

          {/* 1. Problem */}
          <section className="bg-card border border-border rounded-2xl p-6 card-shadow">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-warning/10 flex items-center justify-center shrink-0">
                <AlertCircle size={16} className="text-warning" />
              </div>
              <div>
                <h2 className="text-base font-bold text-foreground">Problem <span className="text-danger">*</span></h2>
                <p className="text-xs text-muted-foreground">Describe the problem clearly. Max 50 words.</p>
              </div>
            </div>
            <textarea
              value={form.problem}
              onChange={e => handleChange('problem', e.target.value)}
              rows={4}
              placeholder="Describe the problem in 50 words or fewer..."
              className={`w-full px-4 py-3 rounded-xl border text-sm text-foreground bg-background placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary transition-all ${errors.problem ? 'border-danger ring-1 ring-danger' : 'border-border'}`}
            />
            <div className="flex items-center justify-between mt-2">
              {errors.problem ? (
                <p className="text-xs text-danger">{errors.problem}</p>
              ) : <span />}
              <span className={`text-xs font-medium tabular-nums ${problemWords > 50 ? 'text-danger' : 'text-muted-foreground'}`}>
                {problemWords}/50 words
              </span>
            </div>

            {/* Category */}
            <div className="mt-4">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                <Tag size={12} className="text-primary" /> Category <span className="text-danger">*</span>
              </label>
              <div className="relative">
                <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <select
                  value={form.category}
                  onChange={e => handleChange('category', e.target.value)}
                  className={`w-full pl-9 pr-4 py-2.5 rounded-xl border text-sm text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary transition-all appearance-none ${errors.category ? 'border-danger ring-1 ring-danger' : 'border-border'}`}
                >
                  <option value="">Select a category...</option>
                  {PROBLEM_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              {errors.category && <p className="text-xs text-danger mt-1">{errors.category}</p>}
            </div>
          </section>

          {/* 2. Location */}
          <section className="bg-card border border-border rounded-2xl p-6 card-shadow">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <MapPin size={16} className="text-primary" />
              </div>
              <div>
                <h2 className="text-base font-bold text-foreground">Location <span className="text-danger">*</span></h2>
                <p className="text-xs text-muted-foreground">Where is this problem occurring?</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">Town / City</label>
                <input
                  type="text"
                  value={form.town}
                  onChange={e => handleChange('town', e.target.value)}
                  placeholder="e.g. Nairobi"
                  className={`w-full px-3 py-2.5 rounded-xl border text-sm text-foreground bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all ${errors.town ? 'border-danger' : 'border-border'}`}
                />
                {errors.town && <p className="text-xs text-danger mt-1">{errors.town}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">Country</label>
                <input
                  type="text"
                  value={form.country}
                  onChange={e => handleChange('country', e.target.value)}
                  placeholder="e.g. Kenya"
                  className={`w-full px-3 py-2.5 rounded-xl border text-sm text-foreground bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all ${errors.country ? 'border-danger' : 'border-border'}`}
                />
                {errors.country && <p className="text-xs text-danger mt-1">{errors.country}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">Continent</label>
                <select
                  value={form.continent}
                  onChange={e => handleChange('continent', e.target.value)}
                  className={`w-full px-3 py-2.5 rounded-xl border text-sm text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary transition-all ${errors.continent ? 'border-danger' : 'border-border'}`}
                >
                  <option value="">Select...</option>
                  {CONTINENTS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                {errors.continent && <p className="text-xs text-danger mt-1">{errors.continent}</p>}
              </div>
            </div>
          </section>

          {/* 3. Date */}
          <section className="bg-card border border-border rounded-2xl p-6 card-shadow">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-info/10 flex items-center justify-center shrink-0">
                <Calendar size={16} className="text-info" />
              </div>
              <div>
                <h2 className="text-base font-bold text-foreground">Date <span className="text-danger">*</span></h2>
                <p className="text-xs text-muted-foreground">When did / will this problem occur?</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">Date Occurred</label>
                <input
                  type="date"
                  value={form.dateOccurred}
                  onChange={e => handleChange('dateOccurred', e.target.value)}
                  className={`w-full px-3 py-2.5 rounded-xl border text-sm text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary transition-all ${errors.dateOccurred ? 'border-danger' : 'border-border'}`}
                />
                {errors.dateOccurred && <p className="text-xs text-danger mt-1">{errors.dateOccurred}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">
                  Expected Future Date <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <input
                  type="date"
                  value={form.dateFuture}
                  min={today}
                  onChange={e => handleChange('dateFuture', e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-border text-sm text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                />
                <p className="text-xs text-muted-foreground mt-1">If the problem is expected to worsen or occur in the future</p>
              </div>
            </div>
          </section>

          {/* 4. Affected Group */}
          <section className="bg-card border border-border rounded-2xl p-6 card-shadow">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-success/10 flex items-center justify-center shrink-0">
                <Users size={16} className="text-success" />
              </div>
              <div>
                <h2 className="text-base font-bold text-foreground">Affected Group <span className="text-danger">*</span></h2>
                <p className="text-xs text-muted-foreground">Who is affected by this problem? Max 50 words.</p>
              </div>
            </div>
            <textarea
              value={form.affectedGroup}
              onChange={e => handleChange('affectedGroup', e.target.value)}
              rows={3}
              placeholder="e.g. Small-scale farmers in rural areas who lack access to irrigation systems..."
              className={`w-full px-4 py-3 rounded-xl border text-sm text-foreground bg-background placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary transition-all ${errors.affectedGroup ? 'border-danger ring-1 ring-danger' : 'border-border'}`}
            />
            <div className="flex items-center justify-between mt-2">
              {errors.affectedGroup ? (
                <p className="text-xs text-danger">{errors.affectedGroup}</p>
              ) : <span />}
              <span className={`text-xs font-medium tabular-nums ${affectedWords > 50 ? 'text-danger' : 'text-muted-foreground'}`}>
                {affectedWords}/50 words
              </span>
            </div>
          </section>

          {/* 5. Effects */}
          <section className="bg-card border border-border rounded-2xl p-6 card-shadow">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-danger/10 flex items-center justify-center shrink-0">
                <Zap size={16} className="text-danger" />
              </div>
              <div>
                <h2 className="text-base font-bold text-foreground">Effects <span className="text-danger">*</span></h2>
                <p className="text-xs text-muted-foreground">What are the consequences of this problem?</p>
              </div>
            </div>
            <textarea
              value={form.effects}
              onChange={e => handleChange('effects', e.target.value)}
              rows={4}
              placeholder="Describe the social, economic, environmental or other effects of this problem..."
              className={`w-full px-4 py-3 rounded-xl border text-sm text-foreground bg-background placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary transition-all ${errors.effects ? 'border-danger ring-1 ring-danger' : 'border-border'}`}
            />
            {errors.effects && <p className="text-xs text-danger mt-2">{errors.effects}</p>}
          </section>

          {/* 6. Current Workaround */}
          <section className="bg-card border border-border rounded-2xl p-6 card-shadow">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-warning/10 flex items-center justify-center shrink-0">
                <Wrench size={16} className="text-warning" />
              </div>
              <div>
                <h2 className="text-base font-bold text-foreground">Current Workaround <span className="text-danger">*</span></h2>
                <p className="text-xs text-muted-foreground">What are people currently doing to cope with this problem?</p>
              </div>
            </div>
            <textarea
              value={form.workaround}
              onChange={e => handleChange('workaround', e.target.value)}
              rows={4}
              placeholder="Describe any existing workarounds, temporary fixes, or coping mechanisms people are using..."
              className={`w-full px-4 py-3 rounded-xl border text-sm text-foreground bg-background placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary transition-all ${errors.workaround ? 'border-danger ring-1 ring-danger' : 'border-border'}`}
            />
            {errors.workaround && <p className="text-xs text-danger mt-2">{errors.workaround}</p>}
          </section>

          {/* 7. Proposed Solution */}
          <section className="bg-card border border-border rounded-2xl p-6 card-shadow">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Lightbulb size={16} className="text-primary" />
              </div>
              <div>
                <h2 className="text-base font-bold text-foreground">Proposed Solution <span className="text-danger">*</span></h2>
                <p className="text-xs text-muted-foreground">Do you have an idea for how this could be solved?</p>
              </div>
            </div>
            <textarea
              value={form.proposedSolution}
              onChange={e => handleChange('proposedSolution', e.target.value)}
              rows={4}
              placeholder="Share your thoughts on how this problem could be addressed or solved..."
              className={`w-full px-4 py-3 rounded-xl border text-sm text-foreground bg-background placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary transition-all ${errors.proposedSolution ? 'border-danger ring-1 ring-danger' : 'border-border'}`}
            />
            {errors.proposedSolution && <p className="text-xs text-danger mt-2">{errors.proposedSolution}</p>}
          </section>

          {/* 8. Picture */}
          <section className="bg-card border border-border rounded-2xl p-6 card-shadow">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                <ImagePlus size={16} className="text-primary" />
              </div>
              <div>
                <h2 className="text-base font-bold text-foreground">Picture <span className="text-muted-foreground font-normal text-sm">(optional)</span></h2>
                <p className="text-xs text-muted-foreground">Upload an image that illustrates the problem.</p>
              </div>
            </div>

            {previewUrl ? (
              <div className="relative rounded-xl overflow-hidden border border-border">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={previewUrl} alt="Problem illustration preview" className="w-full max-h-64 object-cover" />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-foreground/70 hover:bg-foreground flex items-center justify-center transition-colors"
                  aria-label="Remove image"
                >
                  <X size={14} className="text-white" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-border hover:border-primary rounded-xl py-10 flex flex-col items-center gap-3 text-muted-foreground hover:text-primary transition-all group"
              >
                <div className="w-12 h-12 rounded-full bg-muted group-hover:bg-primary/10 flex items-center justify-center transition-colors">
                  <ImagePlus size={22} />
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold">Click to upload an image</p>
                  <p className="text-xs mt-0.5">PNG, JPG, WEBP up to 10MB</p>
                </div>
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleFileChange}
              className="hidden"
              aria-label="Upload problem image"
            />
          </section>

          {/* Submit */}
          <div className="flex items-center justify-between pt-2 pb-8">
            <Link href="/" className="px-5 py-2.5 text-sm font-semibold border border-border text-foreground rounded-xl hover:bg-muted transition-colors">
              Cancel
            </Link>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 px-8 py-2.5 text-sm font-bold bg-primary text-primary-foreground rounded-xl hover:bg-cyan-600 active:scale-95 transition-all shadow-md disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
            >
              {submitting && <Loader2 size={15} className="animate-spin" />}
              {submitting ? 'Posting...' : 'Post Problem'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
